<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'submit'])

const store = useStore()

const firebaseUser = computed(() => store.getters['user/getUser'])
const member = computed(() => store.getters['user/getMember'])

const existingUserName = computed(() => member.value?.display_name || firebaseUser.value?.displayName || '')
const existingUserAvatar = computed(() => member.value?.avatar_url || firebaseUser.value?.photoURL || '')
const existingUserEmail = computed(() => member.value?.email || firebaseUser.value?.email || '')

const userInitials = computed(() => {
  const name = existingUserName.value
  const email = existingUserEmail.value

  if (name) {
    const names = name.split(' ')
    const first = names[0]?.charAt(0) || ''
    const last = names[names.length - 1]?.charAt(0) || ''
    return (first + last).toUpperCase()
  } else if (email) {
    return email.charAt(0).toUpperCase()
  }
  return 'V'
})

const formStep = ref(1)
const maxSteps = 2
const inProgress = ref(false)

const formSchema = toTypedSchema(z.object({
  orgName: z.string().min(3, 'Organization name must be at least 3 characters').max(255),
  userName: z.string().min(2, 'Name must be at least 2 characters').max(100).default(existingUserName.value)
}))

const handleNextStep = () => {
  if (formStep.value < maxSteps) {
    formStep.value++
  }
}

const handlePrevStep = () => {
  if (formStep.value > 1) {
    formStep.value--
  }
}

const handleFormSubmit = async (values) => {
  console.log('Creating organization with values:', values)

  inProgress.value = true

  try {
    const result = await store.dispatch('user/signUpAndCreateOrg', {
      display_name: values.userName || existingUserName.value,
      org_name: values.orgName
    })

    if (result.success) {
      // Update the member name if it was changed
      if (values.userName && values.userName !== existingUserName.value) {
        await store.dispatch('user/update', {
          displayName: values.userName
        })
      }

      emit('submit', { success: true, member: result.member })
      handleCloseForm()
    } else {
      // Error is already handled in the action via alerts
      inProgress.value = false
    }
  } catch (error) {
    console.error('Form submission error:', error)
    inProgress.value = false
  }
}

const handleCloseForm = () => {
  formStep.value = 1
  emit('close')
}

const handleSkipStep = () => {
  emit('submit', {})
  handleCloseForm()
}
</script>

<template>
  <Dialog :open="open" @update:open="handleCloseForm">
    <DialogContent class="max-w-md mx-auto p-4">
      <DialogHeader>
        <DialogTitle>Create Organization</DialogTitle>
      </DialogHeader>

      <Form :validation-schema="formSchema" @submit="handleFormSubmit">
        <!-- Step 1: Organization Name -->
        <div v-show="formStep === 1" class="space-y-4">
          <FormField v-slot="{ componentField }" name="orgName">
            <FormItem>
              <FormLabel>What's the name of your organization?</FormLabel>
              <FormControl>
                <Input
                  v-bind="componentField"
                  placeholder="Ex: Acme Marketing or Acme Co"
                  :disabled="inProgress"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <!-- Step 2: User Details -->
        <div v-show="formStep === 2" class="space-y-4">
          <h3 class="font-bold mb-1">Your personal information</h3>
          <FormField v-slot="{ componentField }" name="userName">
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input
                  v-bind="componentField"
                  :placeholder="existingUserName || 'John Doe'"
                  :value="existingUserName"
                  :disabled="inProgress"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <div>
            <h3 class="text-sm font-medium text-gray-700 mb-1">Profile Photo (optional)</h3>
            <div class="flex items-center gap-4">
              <Avatar class="w-16 h-16">
                <AvatarImage :src="existingUserAvatar" />
                <AvatarFallback>{{ userInitials }}</AvatarFallback>
              </Avatar>
              <!-- <div class="flex flex-col gap-2">
                <Button variant="outline" type="button">
                  {{ existingUserAvatar ? 'Change Photo' : 'Upload Photo' }}
                </Button>
              </div> -->
            </div>
          </div>
        </div>

        <!-- Form Navigation -->
        <div class="flex justify-between items-center mt-6">
          <div class="flex gap-2">
            <Button
              v-if="formStep === 3"
              variant="ghost"
              type="button"
              @click="handleSkipStep"
              :disabled="inProgress"
            >
              Skip this step
            </Button>
          </div>

          <div class="flex gap-2">
            <Button
              v-if="formStep > 1"
              variant="ghost"
              type="button"
              @click="handlePrevStep"
              :disabled="inProgress"
            >
              Back
            </Button>

            <Button
              v-if="formStep < maxSteps"
              type="button"
              @click="handleNextStep"
              :disabled="inProgress"
            >
              Next
            </Button>

            <Button
              v-if="formStep === maxSteps"
              type="submit"
              :disabled="inProgress"
            >
              <span v-if="!inProgress">Create</span>
              <span v-else>Creating...</span>
            </Button>
          </div>
        </div>
      </Form>
    </DialogContent>
  </Dialog>
</template>
